import React, {useEffect, useState} from "react";
import {styled} from "@mui/material/styles";
import {
    Box,
    Paper,
    Typography,
    Card,
    CardContent,
    Grid,
    Tabs,
    Tab,
    TextField,
    Button,
    Switch,
    FormControlLabel,
    MenuItem,
    Select,
    FormControl,
    InputLabel,
    InputAdornment,
    CircularProgress,
    Backdrop,
    Chip,
} from "@mui/material";
import {DataGrid} from "@mui/x-data-grid";
import {
    AccountBalanceOutlined,
    PhoneAndroidOutlined,
    AccountBalanceWalletOutlined,
    TrendingUpOutlined,
    PaymentsOutlined,
} from "@mui/icons-material";
import {payoutsApi} from "../../services/api";
import {getLandlordId} from "../../services/api/client";

const MainContainer = styled(Box)(({theme}) => ({
    zoom: "80%",
}));

const ContentPaper = styled(Paper)(({theme}) => ({
    padding: theme.spacing(3),
    borderRadius: theme.spacing(1),
    boxShadow: "0 0 10px rgba(0, 0, 0, 0.05)",
    marginBottom: theme.spacing(3),
}));

const BalanceCard = styled(Card)(({theme, bgcolor}) => ({
    backgroundColor: bgcolor || "#fff",
    color: bgcolor ? "#fff" : "inherit",
    borderRadius: theme.spacing(1.5),
    boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
    height: "100%",
}));

const COLORS = {
    primary: "#2C5283",
    secondary: "#E2684F",
    tertiary: "#4CAF50",
    background: "#F5F5F5",
};

function Payouts({snackBar, refreshSideBar}) {
    const landlordId = getLandlordId();

    // Loading states
    const [isLoading, setIsLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);
    const [isRequesting, setIsRequesting] = useState(false);

    // Balance data
    const [balances, setBalances] = useState({
        pending_balance: 0,
        upcoming_balance: 0,
        total_available: 0,
    });

    // Settings data
    const [payoutMethod, setPayoutMethod] = useState("BANK");
    const [banks, setBanks] = useState([]);
    const [settings, setSettings] = useState({
        bank_code: "",
        bank_account_number: "",
        bank_account_name: "",
        phone_number: "",
        registered_name: "",
        auto_disburse: false,
        minimum_payout_amount: 1000,
    });

    // Payout request
    const [payoutAmount, setPayoutAmount] = useState("");

    // History data
    const [history, setHistory] = useState([]);
    const [historyPage, setHistoryPage] = useState(0);
    const [historyTotal, setHistoryTotal] = useState(0);
    const [pageSize, setPageSize] = useState(10);

    // Fetch initial data
    useEffect(() => {
        if (refreshSideBar) {
            refreshSideBar();
        }
        fetchData();
    }, []);

    // Fetch history when page changes
    useEffect(() => {
        fetchHistory();
    }, [historyPage, pageSize]);

    const fetchData = async () => {
        setIsLoading(true);
        try {
            // Fetch settings and banks in parallel
            const [settingsRes, banksRes] = await Promise.all([
                payoutsApi.getSettings(landlordId),
                payoutsApi.getBanks(),
            ]);

            if (settingsRes.ok && settingsRes.data) {
                const data = settingsRes.data;
                setBalances({
                    pending_balance: data.pending_balance || 0,
                    upcoming_balance: data.upcoming_balance || 0,
                    total_available: data.total_available || 0,
                });
                setPayoutMethod(data.payout_method || "BANK");
                setSettings({
                    bank_code: data.bank_code || "",
                    bank_account_number: data.bank_account_number || "",
                    bank_account_name: data.bank_account_name || "",
                    phone_number: data.phone_number || "",
                    registered_name: data.registered_name || "",
                    auto_disburse: data.auto_disburse || false,
                    minimum_payout_amount: data.minimum_payout_amount || 1000,
                });
            }

            if (banksRes.ok && banksRes.data) {
                setBanks(Array.isArray(banksRes.data) ? banksRes.data : banksRes.data.banks || []);
            }

            await fetchHistory();
        } catch (error) {
            console.error("Error fetching payout data:", error);
            snackBar?.({
                text: "Failed to load payout settings",
                type: "error",
            });
        } finally {
            setIsLoading(false);
        }
    };

    const fetchHistory = async () => {
        try {
            const historyRes = await payoutsApi.getHistory(
                landlordId,
                historyPage + 1,
                pageSize
            );
            if (historyRes.ok && historyRes.data) {
                const historyData = Array.isArray(historyRes.data)
                    ? historyRes.data
                    : historyRes.data.payouts || [];
                setHistory(historyData.map((item, index) => ({...item, id: item.id || index})));
                setHistoryTotal(historyRes.data.total || historyData.length);
            }
        } catch (error) {
            console.error("Error fetching payout history:", error);
        }
    };

    const handleSaveSettings = async () => {
        setIsSaving(true);
        try {
            const data = {
                landlord_id: landlordId,
                payout_method: payoutMethod,
                auto_disburse: settings.auto_disburse,
                minimum_payout_amount: settings.minimum_payout_amount,
            };

            if (payoutMethod === "BANK") {
                data.bank_code = settings.bank_code;
                data.bank_account_number = settings.bank_account_number;
                data.bank_account_name = settings.bank_account_name;
            } else {
                data.phone_number = settings.phone_number;
                data.registered_name = settings.registered_name;
            }

            const res = await payoutsApi.updateSettings(data);
            if (res.ok) {
                snackBar?.({
                    text: "Payout settings saved successfully",
                    type: "success",
                });
            } else {
                snackBar?.({
                    text: res.data?.message || "Failed to save settings",
                    type: "error",
                });
            }
        } catch (error) {
            console.error("Error saving settings:", error);
            snackBar?.({
                text: "Failed to save settings",
                type: "error",
            });
        } finally {
            setIsSaving(false);
        }
    };

    const handleRequestPayout = async () => {
        const amount = payoutAmount ? parseFloat(payoutAmount) : null;

        const minPayout = settings.minimum_payout_amount || 0;

        if (amount !== null && amount < minPayout) {
            snackBar?.({
                text: `Minimum payout amount is KES ${minPayout.toLocaleString()}`,
                type: "error",
            });
            return;
        }

        if (balances.pending_balance < minPayout) {
            snackBar?.({
                text: "Insufficient balance for payout",
                type: "error",
            });
            return;
        }

        setIsRequesting(true);
        try {
            const res = await payoutsApi.requestPayout(landlordId, amount);
            if (res.ok) {
                snackBar?.({
                    text: "Payout requested successfully",
                    type: "success",
                });
                setPayoutAmount("");
                fetchData(); // Refresh data
            } else {
                snackBar?.({
                    text: res.data?.message || "Failed to request payout",
                    type: "error",
                });
            }
        } catch (error) {
            console.error("Error requesting payout:", error);
            snackBar?.({
                text: "Failed to request payout",
                type: "error",
            });
        } finally {
            setIsRequesting(false);
        }
    };

    const handleSettingChange = (field) => (event) => {
        const value = event.target.type === "checkbox" ? event.target.checked : event.target.value;
        setSettings((prev) => ({...prev, [field]: value}));
    };

    const formatCurrency = (amount) => {
        return `KES ${Number(amount || 0).toLocaleString("en-KE", {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
        })}`;
    };

    const getStatusChip = (status) => {
        const statusConfig = {
            completed: {color: "success", label: "Completed"},
            pending: {color: "warning", label: "Pending"},
            processing: {color: "info", label: "Processing"},
            failed: {color: "error", label: "Failed"},
        };
        const config = statusConfig[status?.toLowerCase()] || {color: "default", label: status};
        return <Chip size="small" color={config.color} label={config.label}/>;
    };

    const historyColumns = [
        {
            field: "date_created",
            headerName: "Date",
            flex: 1,
            minWidth: 120,
            valueFormatter: (params) => {
                if (!params.value) return "-";
                return new Date(params.value).toLocaleDateString("en-KE", {
                    year: "numeric",
                    month: "short",
                    day: "numeric",
                });
            },
        },
        {
            field: "amount",
            headerName: "Amount",
            flex: 1,
            minWidth: 120,
            valueFormatter: (params) => formatCurrency(params.value),
        },
        {
            field: "payout_method",
            headerName: "Method",
            flex: 0.8,
            minWidth: 100,
        },
        {
            field: "destination",
            headerName: "Destination",
            flex: 1.2,
            minWidth: 150,
            valueGetter: (params) => {
                const row = params.row;
                if (row.payout_method === "MPESA") {
                    return row.phone_number || "-";
                }
                return row.account_number || "-";
            },
        },
        {
            field: "status",
            headerName: "Status",
            flex: 0.8,
            minWidth: 100,
            renderCell: (params) => getStatusChip(params.value),
        },
    ];

    if (isLoading) {
        return (
            <Backdrop open={true} sx={{color: "#fff", zIndex: 9999}}>
                <CircularProgress color="inherit"/>
            </Backdrop>
        );
    }

    return (
        <MainContainer>
            {/* Page Header */}
            <Box sx={{mb: 3}}>
                <Typography variant="h5" fontWeight="bold" color={COLORS.primary}>
                    Payouts
                </Typography>
                <Typography variant="body2" color="textSecondary">
                    Configure your payout settings and request withdrawals
                </Typography>
            </Box>

            {/* Balance Cards */}
            <Grid container spacing={3} sx={{mb: 3}}>
                <Grid item xs={12} sm={4}>
                    <BalanceCard bgcolor={COLORS.primary}>
                        <CardContent>
                            <Box display="flex" alignItems="center" gap={1} mb={1}>
                                <AccountBalanceWalletOutlined/>
                                <Typography variant="body2">Available for Payout</Typography>
                            </Box>
                            <Typography variant="h4" fontWeight="bold">
                                {formatCurrency(balances.pending_balance)}
                            </Typography>
                        </CardContent>
                    </BalanceCard>
                </Grid>
                <Grid item xs={12} sm={4}>
                    <BalanceCard bgcolor={COLORS.secondary}>
                        <CardContent>
                            <Box display="flex" alignItems="center" gap={1} mb={1}>
                                <TrendingUpOutlined/>
                                <Typography variant="body2">Coming Tomorrow</Typography>
                            </Box>
                            <Typography variant="h4" fontWeight="bold">
                                {formatCurrency(balances.upcoming_balance)}
                            </Typography>
                        </CardContent>
                    </BalanceCard>
                </Grid>
                <Grid item xs={12} sm={4}>
                    <BalanceCard bgcolor={COLORS.tertiary}>
                        <CardContent>
                            <Box display="flex" alignItems="center" gap={1} mb={1}>
                                <PaymentsOutlined/>
                                <Typography variant="body2">Total Available</Typography>
                            </Box>
                            <Typography variant="h4" fontWeight="bold">
                                {formatCurrency(balances.total_available)}
                            </Typography>
                        </CardContent>
                    </BalanceCard>
                </Grid>
            </Grid>

            {/* Payout Settings */}
            <ContentPaper>
                <Typography variant="h6" fontWeight="bold" mb={2}>
                    Payout Settings
                </Typography>

                {/* Payout Method Tabs */}
                <Tabs
                    value={payoutMethod}
                    onChange={(e, newValue) => setPayoutMethod(newValue)}
                    sx={{mb: 3}}
                >
                    <Tab
                        value="BANK"
                        label="Bank Transfer"
                        icon={<AccountBalanceOutlined/>}
                        iconPosition="start"
                    />
                    <Tab
                        value="MPESA"
                        label="M-PESA"
                        icon={<PhoneAndroidOutlined/>}
                        iconPosition="start"
                    />
                </Tabs>

                {/* Bank Form */}
                {payoutMethod === "BANK" && (
                    <Grid container spacing={2} sx={{mb: 3}}>
                        <Grid item xs={12} sm={4}>
                            <FormControl fullWidth>
                                <InputLabel>Bank</InputLabel>
                                <Select
                                    value={settings.bank_code}
                                    onChange={handleSettingChange("bank_code")}
                                    label="Bank"
                                >
                                    {banks.map((bank, index) => (
                                        <MenuItem key={bank.bank_code || bank.id || index}
                                                  value={bank.bank_code || bank.id}>
                                            {bank.bank_name}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={4}>
                            <TextField
                                fullWidth
                                label="Account Number"
                                value={settings.bank_account_number}
                                onChange={handleSettingChange("bank_account_number")}
                            />
                        </Grid>
                        <Grid item xs={12} sm={4}>
                            <TextField
                                fullWidth
                                label="Account Name"
                                value={settings.bank_account_name}
                                onChange={handleSettingChange("bank_account_name")}
                            />
                        </Grid>
                    </Grid>
                )}

                {/* M-PESA Form */}
                {payoutMethod === "MPESA" && (
                    <Grid container spacing={2} sx={{mb: 3}}>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                label="Phone Number"
                                value={settings.phone_number}
                                onChange={handleSettingChange("phone_number")}
                                placeholder="254XXXXXXXXX"
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                label="Registered Name"
                                value={settings.registered_name}
                                onChange={handleSettingChange("registered_name")}
                            />
                        </Grid>
                    </Grid>
                )}

                {/* Auto-disburse and Minimum */}
                <Grid container spacing={2} alignItems="center" sx={{mb: 3}}>
                    <Grid item xs={12} sm={6}>
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={settings.auto_disburse}
                                    onChange={handleSettingChange("auto_disburse")}
                                    color="primary"
                                />
                            }
                            label="Enable Auto-Disbursement"
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <TextField
                            fullWidth
                            label="Minimum Payout Amount"
                            type="number"
                            value={settings.minimum_payout_amount}
                            onChange={handleSettingChange("minimum_payout_amount")}
                            InputProps={{
                                startAdornment: <InputAdornment position="start">KES</InputAdornment>,
                            }}
                        />
                    </Grid>
                </Grid>

                <Button
                    variant="contained"
                    onClick={handleSaveSettings}
                    disabled={isSaving}
                    sx={{backgroundColor: COLORS.primary}}
                >
                    {isSaving ? <CircularProgress size={24} color="inherit"/> : "Save Settings"}
                </Button>
            </ContentPaper>

            {/* Request Payout */}
            <ContentPaper>
                <Typography variant="h6" fontWeight="bold" mb={2}>
                    Request Payout
                </Typography>
                <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12} sm={6}>
                        <TextField
                            fullWidth
                            label="Amount (optional - leave empty for full balance)"
                            type="number"
                            value={payoutAmount}
                            onChange={(e) => setPayoutAmount(e.target.value)}
                            InputProps={{
                                startAdornment: <InputAdornment position="start">KES</InputAdornment>,
                            }}
                            helperText={`Minimum: KES ${(settings.minimum_payout_amount || 0).toLocaleString()}`}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <Button
                            variant="contained"
                            onClick={handleRequestPayout}
                            disabled={isRequesting || balances.pending_balance < (settings.minimum_payout_amount || 0)}
                            sx={{backgroundColor: COLORS.secondary, height: 56}}
                            fullWidth
                        >
                            {isRequesting ? (
                                <CircularProgress size={24} color="inherit"/>
                            ) : (
                                "Request Payout"
                            )}
                        </Button>
                    </Grid>
                </Grid>
                {balances.pending_balance < (settings.minimum_payout_amount || 0) && (
                    <Typography variant="body2" color="error" sx={{mt: 1}}>
                        Insufficient balance. Minimum required: KES {(settings.minimum_payout_amount || 0).toLocaleString()}
                    </Typography>
                )}
            </ContentPaper>

            {/* Payout History */}
            <ContentPaper>
                <Typography variant="h6" fontWeight="bold" mb={2}>
                    Payout History
                </Typography>
                <Box sx={{height: 400, width: "100%"}}>
                    <DataGrid
                        rows={history}
                        columns={historyColumns}
                        pageSize={pageSize}
                        rowsPerPageOptions={[5, 10, 25]}
                        onPageSizeChange={(newPageSize) => setPageSize(newPageSize)}
                        page={historyPage}
                        onPageChange={(newPage) => setHistoryPage(newPage)}
                        rowCount={historyTotal}
                        paginationMode="server"
                        disableSelectionOnClick
                        sx={{
                            border: "none",
                            "& .MuiDataGrid-columnHeaders": {
                                backgroundColor: COLORS.background,
                                fontWeight: "bold",
                            },
                        }}
                    />
                </Box>
            </ContentPaper>
        </MainContainer>
    );
}

export default Payouts;
